import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getNavFor } from "@/lib/nav";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  AreaChart, Area, CartesianGrid,
} from "recharts";
import { Zap, Database, TrendingDown, Search, Gauge, MonitorSmartphone } from "lucide-react";

const COLORS = ["#0284C7", "#059669", "#D97706", "#7c3aed", "#e11d48"];

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <Card className="p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <div className="mt-2 font-heading text-3xl font-extrabold tracking-tight text-foreground">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </Card>
  );
}

export default function Analytics() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("all");

  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setD(null);
    const url = branchId && branchId !== "all" ? `/analytics?branch_id=${branchId}` : "/analytics";
    api.get(url).then((r) => setD(r.data)).catch(() => {});
  }, [branchId]);

  const nav = getNavFor(user);
  const showTest = user?.role === "super_admin" || user?.role === "hod";

  return (
    <Layout nav={nav} showTestButton={showTest}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground">Usage Analytics</h1>
          <p className="mt-1 text-sm text-muted-foreground">How references and the knowledge base are utilised across the 4 branches.</p>
        </div>
        <div className="w-full sm:w-64">
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger data-testid="analytics-branch-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!d ? (
        <p className="mt-8 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="mt-8 space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat icon={Search} label="Total Searches" value={d.total_searches} accent="text-sky-600" />
            <Stat icon={Zap} label="Cache Hits" value={d.cached} sub={`${d.total_searches ? Math.round((d.cached / d.total_searches) * 100) : 0}% served instantly`} accent="text-emerald-600" />
            <Stat icon={TrendingDown} label="Crawls Saved" value={d.crawls_saved} sub="re-crawls avoided by pooling" accent="text-amber-600" />
            <Stat icon={Gauge} label="Avg Fresh vs Cached" value={`${d.avg_fresh_ms}/${d.avg_cached_ms}ms`} sub="fresh lookup vs pooled cache" accent="text-violet-600" />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Card className="p-5 lg:col-span-2">
              <h3 className="font-heading text-base font-bold text-foreground">Searches over last 7 days</h3>
              <div className="mt-4 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={d.timeline}>
                    <defs>
                      <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0284C7" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#0284C7" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="date" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip />
                    <Area type="monotone" dataKey="searches" stroke="#0284C7" strokeWidth={2} fill="url(#g1)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-5">
              <h3 className="font-heading text-base font-bold text-foreground">Searches by branch</h3>
              <div className="mt-4 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={d.per_branch.length ? d.per_branch : [{ name: "No data", value: 1 }]} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                      {(d.per_branch.length ? d.per_branch : [{ name: "No data", value: 1 }]).map((entry, i) => <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card className="p-5">
              <h3 className="font-heading text-base font-bold text-foreground">Reference usage</h3>
              <div className="mt-4 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={d.resource_usage.length ? d.resource_usage : [{ name: "No data", value: 0 }]} layout="vertical" margin={{ left: 20 }}>
                    <XAxis type="number" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" fontSize={11} width={140} tickLine={false} axisLine={false} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#059669" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-5">
              <h3 className="font-heading text-base font-bold text-foreground">Top pooled queries</h3>
              <div className="mt-4 space-y-2">
                {d.top_queries.length === 0 && <p className="text-sm text-muted-foreground">No queries yet.</p>}
                {d.top_queries.map((q, i) => (
                  <div key={`${q.query}-${i}`} className="flex items-center justify-between gap-2 rounded-lg bg-muted/40 px-3 py-2 text-sm">
                    <span className="truncate text-foreground">{q.query}</span>
                    <span className="flex shrink-0 items-center gap-1 font-mono-c text-xs font-semibold text-emerald-600"><Zap className="h-3 w-3" />{q.hits}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2 rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-700 dark:bg-sky-950 dark:text-sky-300">
                <Database className="h-4 w-4" /> {d.cache_entries} answers pooled &amp; reusable across all branches.
              </div>
            </Card>
          </div>

          <Card className="p-5">
            <div className="flex items-center gap-1.5">
              <MonitorSmartphone className="h-4 w-4 text-violet-600" />
              <h3 className="font-heading text-base font-bold text-foreground">Searches by desk / account</h3>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Each branch account is shared across its dispensing desks.</p>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.per_desk?.length ? d.per_desk : [{ name: "No data", value: 0 }]} margin={{ bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={false} interval={0} angle={-12} textAnchor="end" height={50} />
                  <YAxis fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#7c3aed" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}
    </Layout>
  );
}
